import { useCallback, useEffect, useRef, useState } from 'react';

const DRAG_THRESHOLD = 10;
const KEYBOARD_STEP = 5;
const FINE_POINTER_QUERY = '(hover: hover) and (pointer: fine)';

function createInitialGestureState() {
  return {
    startX: 0,
    startY: 0,
    pointerId: null,
    isActive: false,
    scrollIntent: false,
  };
}

/**
 * @param {{
 *   containerRef: import('react').RefObject<HTMLElement | null>;
 *   handleRef?: import('react').RefObject<HTMLElement | null>;
 *   position: number;
 *   setPosition: (value: number) => void;
 *   onUserInteraction?: () => void;
 *   interactionMode?: 'hover' | 'drag';
 * }} params
 */
export function useBeforeAfterSliderInteraction({
  containerRef,
  handleRef,
  position,
  setPosition,
  onUserInteraction,
  interactionMode = 'hover',
}) {
  const isDragMode = interactionMode === 'drag';

  const [isFinePointer, setIsFinePointer] = useState(() => {
    if (typeof window === 'undefined') return true;
    return window.matchMedia(FINE_POINTER_QUERY).matches;
  });
  const [isSliding, setIsSliding] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  const isPointerInsideRef = useRef(false);
  const gestureRef = useRef(createInitialGestureState());
  const hasUserInteractedRef = useRef(false);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const mediaQuery = window.matchMedia(FINE_POINTER_QUERY);
    const handleChange = (event) => {
      setIsFinePointer(event.matches);
    };

    setIsFinePointer(mediaQuery.matches);
    mediaQuery.addEventListener?.('change', handleChange);
    mediaQuery.addListener?.(handleChange);

    return () => {
      mediaQuery.removeEventListener?.('change', handleChange);
      mediaQuery.removeListener?.(handleChange);
    };
  }, []);

  const notifyUserInteraction = useCallback(() => {
    if (hasUserInteractedRef.current) return;
    hasUserInteractedRef.current = true;
    onUserInteraction?.();
  }, [onUserInteraction]);

  const updatePositionFromClientX = useCallback(
    (clientX) => {
      const rect = containerRef.current?.getBoundingClientRect();
      if (!rect || rect.width <= 0) return;

      const percentage = ((clientX - rect.left) / rect.width) * 100;
      setPosition(Math.max(0, Math.min(100, percentage)));
      notifyUserInteraction();
    },
    [containerRef, setPosition, notifyUserInteraction],
  );

  const releasePointerCapture = useCallback(
    (pointerId) => {
      const target = handleRef?.current || containerRef.current;
      if (pointerId != null && target?.hasPointerCapture?.(pointerId)) {
        target.releasePointerCapture(pointerId);
      }
    },
    [containerRef, handleRef],
  );

  const resetGesture = useCallback(
    (event) => {
      const gesture = gestureRef.current;
      if (gesture.isActive && gesture.pointerId != null) {
        releasePointerCapture(gesture.pointerId);
      }
      gestureRef.current = createInitialGestureState();
      setIsSliding(false);
      setIsDragging(false);

      if (event?.type === 'pointerleave' && isFinePointer && !isDragMode) {
        isPointerInsideRef.current = false;
      }
    },
    [isFinePointer, isDragMode, releasePointerCapture],
  );

  const handlePointerEnter = useCallback(() => {
    if (isDragMode) return;
    isPointerInsideRef.current = true;
  }, [isDragMode]);

  const handlePointerLeave = useCallback(
    (event) => {
      if (isDragMode) return;
      isPointerInsideRef.current = false;
      if (!gestureRef.current.isActive) {
        setIsSliding(false);
        return;
      }
      resetGesture(event);
    },
    [isDragMode, resetGesture],
  );

  const handlePointerDown = useCallback(
    (event) => {
      if (isDragMode) {
        setIsDragging(true);
        gestureRef.current = {
          startX: event.clientX,
          startY: event.clientY,
          pointerId: event.pointerId,
          isActive: true,
          scrollIntent: false,
        };
        event.currentTarget.setPointerCapture?.(event.pointerId);
        setIsSliding(true);
        updatePositionFromClientX(event.clientX);
        return;
      }

      if (isFinePointer) return;

      gestureRef.current = {
        startX: event.clientX,
        startY: event.clientY,
        pointerId: event.pointerId,
        isActive: false,
        scrollIntent: false,
      };
    },
    [isDragMode, isFinePointer, updatePositionFromClientX],
  );

  const handlePointerMove = useCallback(
    (event) => {
      if (isDragMode) {
        if (!isDragging) return;
        event.preventDefault();
        setIsSliding(true);
        updatePositionFromClientX(event.clientX);
        return;
      }

      if (isFinePointer) {
        if (!isPointerInsideRef.current) return;
        setIsSliding(true);
        updatePositionFromClientX(event.clientX);
        return;
      }

      const gesture = gestureRef.current;
      if (gesture.pointerId !== event.pointerId) return;

      if (gesture.scrollIntent) return;

      if (gesture.isActive) {
        event.preventDefault();
        setIsSliding(true);
        updatePositionFromClientX(event.clientX);
        return;
      }

      const deltaX = event.clientX - gesture.startX;
      const deltaY = event.clientY - gesture.startY;

      if (Math.abs(deltaX) < DRAG_THRESHOLD && Math.abs(deltaY) < DRAG_THRESHOLD) {
        return;
      }

      if (Math.abs(deltaY) > Math.abs(deltaX)) {
        gesture.scrollIntent = true;
        return;
      }

      if (Math.abs(deltaX) >= DRAG_THRESHOLD) {
        gesture.isActive = true;
        containerRef.current?.setPointerCapture?.(event.pointerId);
        event.preventDefault();
        setIsSliding(true);
        updatePositionFromClientX(event.clientX);
      }
    },
    [containerRef, isDragMode, isDragging, isFinePointer, updatePositionFromClientX],
  );

  const handlePointerUp = useCallback(
    (event) => {
      if (isDragMode) {
        if (event.currentTarget.hasPointerCapture?.(event.pointerId)) {
          event.currentTarget.releasePointerCapture(event.pointerId);
        }
        setIsDragging(false);
        setIsSliding(false);
        gestureRef.current = createInitialGestureState();
        return;
      }

      if (isFinePointer) {
        setIsSliding(false);
        return;
      }
      resetGesture(event);
    },
    [isDragMode, isFinePointer, resetGesture],
  );

  const handlePointerCancel = useCallback(
    (event) => {
      resetGesture(event);
    },
    [resetGesture],
  );

  const handleKeyDown = useCallback(
    (event) => {
      let nextPosition = null;

      switch (event.key) {
        case 'ArrowLeft':
          nextPosition = position - KEYBOARD_STEP;
          break;
        case 'ArrowRight':
          nextPosition = position + KEYBOARD_STEP;
          break;
        case 'Home':
          nextPosition = 0;
          break;
        case 'End':
          nextPosition = 100;
          break;
        default:
          return;
      }

      event.preventDefault();
      setPosition(Math.max(0, Math.min(100, nextPosition)));
      setIsSliding(true);
      notifyUserInteraction();
    },
    [position, setPosition, notifyUserInteraction],
  );

  const handleKeyUp = useCallback(() => {
    setIsSliding(false);
  }, []);

  const handleBlur = useCallback(() => {
    setIsSliding(false);
    if (isDragMode) {
      setIsDragging(false);
    }
  }, [isDragMode]);

  const containerHandlers = isDragMode
    ? {}
    : {
        onPointerEnter: handlePointerEnter,
        onPointerLeave: handlePointerLeave,
        onPointerDown: handlePointerDown,
        onPointerMove: handlePointerMove,
        onPointerUp: handlePointerUp,
        onPointerCancel: handlePointerCancel,
      };

  const handleDragHandlers = isDragMode
    ? {
        onPointerDown: handlePointerDown,
        onPointerMove: handlePointerMove,
        onPointerUp: handlePointerUp,
        onPointerCancel: handlePointerCancel,
      }
    : {};

  return {
    isSliding,
    containerHandlers,
    handleDragHandlers,
    handleA11yProps: {
      role: 'slider',
      'aria-valuemin': 0,
      'aria-valuemax': 100,
      'aria-valuenow': Math.round(position),
      'aria-label': 'Before and after comparison slider',
      tabIndex: 0,
      onKeyDown: handleKeyDown,
      onKeyUp: handleKeyUp,
      onBlur: handleBlur,
    },
  };
}
